import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Home, Heart, Users, GraduationCap, Star, BookOpen,
  Phone, Mail, Globe, ArrowRight, Sparkles, Target,
  Building2, Megaphone, Briefcase, RefreshCw, DollarSign,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SectionHeading from '../components/SectionHeading';

// pre-fills the donate page with the correct category
const donateHref = (amount, recurring = false) =>
  `/donate?category=place-to-worship${amount ? `&amount=${amount}` : ''}${recurring ? '&recurring=1' : ''}`;

const reasons = [
  {
    icon: Star,
    title: '5 Daily Prayers',
    text: 'A dedicated, permanent space to offer all five daily prayers in congregation.',
  },
  {
    icon: BookOpen,
    title: 'Quran Classes for All Ages',
    text: 'Structured Quran classes for children, youth and adults.',
  },
  {
    icon: Users,
    title: 'Community & Education',
    text: 'Programs, lectures, youth activities and events to strengthen our community.',
  },
  {
    icon: Sparkles,
    title: 'Ready for Ramadan Next Year, In Shaa Allah',
    text: 'Our goal is to be fully set up in time for Ramadan next year and offer Taraweeh prayers.',
  },
];

const provisions = [
  'Permanent place for worship',
  'Classrooms for Islamic education',
  'Programs for youth & adults',
  'Community events & gatherings',
  'A center for unity, learning & growth',
];

const waysToSupport = [
  { icon: RefreshCw,  title: 'Monthly Donations', text: 'The strongest form of support — recurring monthly contributions.', href: donateHref(50, true),  cta: 'Give Monthly' },
  { icon: DollarSign, title: 'One-Time Donations', text: 'Every contribution, big or small, moves us closer to the goal.',   href: donateHref(),         cta: 'Donate Now' },
  { icon: Sparkles,   title: 'Sponsor a Day / Week / Month', text: 'Sponsor rent for a day (~$200), week (~$1,400), or full month ($6,000).', href: donateHref(200), cta: 'Sponsor' },
  { icon: Briefcase,  title: 'Corporate & Business Support', text: 'Partner with us as a business sponsor and support the community.',       href: '/contact',           cta: 'Contact Us' },
  { icon: Megaphone,  title: 'Spread the Word', text: 'Share this campaign with family, friends, and your network. Awareness is sadaqah.', href: '/flyers',            cta: 'View Flyers' },
];

const PlaceToWorshipPage = () => {
  return (
    <div className="min-h-screen pt-24" data-testid="place-to-worship-page">
      {/* Hero */}
      <section className="relative py-16 lg:py-24 overflow-hidden islamic-pattern">
        {/* soft ambient orbs */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-24 right-1/4 w-96 h-96 rounded-full bg-secondary/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/30 mb-6">
              <Home className="h-4 w-4 text-secondary" />
              <span className="text-sm font-medium text-secondary">Fundraising Campaign</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Together, <br/>
              <span className="text-gradient">Let&apos;s Secure a Place to Worship</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mb-8">
              A place to pray. A place to grow. A permanent home for our community in South Barrie.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={donateHref(50, true)} data-testid="hero-monthly-cta">
                <Button size="lg" className="rounded-full text-base h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90 glow-teal">
                  <Heart className="mr-2 h-5 w-5" /> Become a Monthly Supporter
                </Button>
              </Link>
              <Link to={donateHref()} data-testid="hero-onetime-cta">
                <Button size="lg" variant="outline" className="rounded-full text-base h-14 px-8">
                  Give One-Time <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Goal Banner */}
      <section className="py-12 bg-secondary/5 border-y border-secondary/30" data-testid="goal-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="text-center md:text-left">
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">Our Goal</p>
            <p className="text-5xl md:text-6xl font-bold text-secondary font-['Outfit']">$6,000</p>
            <p className="text-sm text-muted-foreground mt-1">per month, ongoing</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-lg text-foreground leading-relaxed">
              We are currently <span className="text-secondary font-semibold">seeking to secure a permanent place in South Barrie</span> so we can serve our growing community. Estimated total rent and expenses are about <span className="text-secondary font-semibold">$6,000/month</span> to run this place.
            </p>
            <p className="mt-3 text-sm text-amber-500 font-medium">
              We need committed individuals to support monthly — otherwise, this opportunity will be gone.
            </p>
          </div>
        </div>
      </section>

      {/* Sadaqah Jariyyah Callout */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="p-8 md:p-10 rounded-3xl bg-gradient-to-br from-primary/10 via-card to-secondary/10 border border-primary/30 text-center"
            data-testid="sadaqah-jariyyah-card"
          >
            <div className="inline-flex p-4 rounded-2xl bg-primary/10 mb-4">
              <Heart className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground font-['Outfit'] mb-4">
              Sadaqah Jariyyah <span className="text-gradient">Opportunity</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-4">
              When you help establish a place of worship, <span className="text-foreground font-medium">every prayer, every lesson, and every good deed</span> done there will continue to benefit you — In Shaa Allah.
            </p>
            <p className="text-primary font-semibold text-lg">A reward that never ends.</p>
            <p className="mt-6 pt-6 border-t border-border text-sm text-muted-foreground italic max-w-xl mx-auto">
              &quot;Whoever builds a mosque for Allah, Allah will build for him a house in Paradise.&quot; — Sahih al-Bukhari
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why We Need This Place */}
      <section className="py-16 lg:py-24 bg-card border-y border-border" data-testid="why-we-need">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading badge="Why We Need This Place" title="A Foundation for Faith &amp; Family" description="Four reasons a permanent place transforms our community" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reasons.map((r, i) => (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-6 rounded-2xl bg-background border border-border hover:border-primary/50 transition-colors"
                data-testid={`reason-${i}`}
              >
                <div className="p-3 rounded-xl bg-primary/10 w-fit mb-4">
                  <r.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold font-['Outfit'] text-foreground mb-2">{r.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{r.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What This Place Will Provide */}
      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading badge="What This Place Will Provide" title="More Than a Building" description="A permanent home enables everything we've been building toward" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="provisions-grid">
            {provisions.map((item, i) => (
              <div key={item} className="flex items-center gap-4 p-5 rounded-2xl bg-card border border-border" data-testid={`provision-${i}`}>
                <div className="p-3 rounded-xl bg-secondary/10 flex-shrink-0">
                  <Building2 className="h-5 w-5 text-secondary" />
                </div>
                <p className="text-foreground font-medium">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-lg text-primary font-semibold mt-8">
            This is our community. This is our future.
          </p>
        </div>
      </section>

      {/* Every Dollar Counts */}
      <section className="py-12 bg-primary/5 border-y border-primary/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Target className="h-8 w-8 text-primary mx-auto mb-3" />
          <h3 className="text-2xl md:text-3xl font-bold text-foreground font-['Outfit'] mb-3">
            Every Dollar Counts. Every Month Makes a Big Difference.
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Be a monthly supporter and help us secure this blessing for generations to come.
          </p>
          <p className="mt-4 text-primary font-semibold uppercase tracking-wider text-sm">
            Small Amount • Big Reward • Eternal Impact
          </p>
        </div>
      </section>

      {/* Ways to Support */}
      <section className="py-16 lg:py-24" data-testid="ways-to-support">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading badge="Ways to Support" title="How You Can Contribute" description="Pick the way that works best for you — every path helps us reach the goal" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {waysToSupport.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-colors flex flex-col"
                data-testid={`support-way-${i}`}
              >
                <div className="p-3 rounded-xl bg-primary/10 w-fit mb-4">
                  <w.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold font-['Outfit'] text-foreground mb-2">{w.title}</h3>
                <p className="text-sm text-muted-foreground mb-6 flex-1">{w.text}</p>
                <Link to={w.href}>
                  <Button variant="outline" className="w-full rounded-full" data-testid={`support-cta-${i}`}>
                    {w.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Suggested Amounts */}
      <section className="py-16 bg-card border-y border-border" data-testid="suggested-amounts">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading badge="Pick an Amount" title="Contribute in Seconds" description="Every recurring donation locks in monthly support toward the $6,000 goal" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[25, 50, 100, 250].map((amt) => (
              <Link key={amt} to={donateHref(amt, true)} data-testid={`amount-${amt}`}>
                <div className="p-6 rounded-2xl bg-background border-2 border-border hover:border-primary hover:bg-primary/5 transition-all text-center cursor-pointer">
                  <p className="text-3xl font-bold text-primary">${amt}</p>
                  <p className="text-xs text-muted-foreground mt-1">per month</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={donateHref(500, true)}>
              <Button variant="outline" className="rounded-full" data-testid="amount-500">$500 / month</Button>
            </Link>
            <Link to={donateHref()}>
              <Button variant="outline" className="rounded-full" data-testid="amount-custom">Custom / One-time</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust / Non-profit note */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-5 rounded-xl bg-muted/30 border border-border text-center">
            <p className="text-sm text-muted-foreground">
              <span className="text-foreground font-medium">TNMC is a non-profit community organization.</span> Our charitable organization application is currently under process with the CRA.
            </p>
          </div>
        </div>
      </section>

      {/* CTA / Contact Footer */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 via-card to-secondary/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground font-['Outfit'] mb-3">
            Join the Campaign. <br/>
            <span className="text-gradient">Be Part of Something Eternal.</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-2">Jazakum Allahu Khairan.</p>
          <p className="text-sm text-muted-foreground mb-8">To pledge or get involved:</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <a href="tel:2494445227" className="flex items-center justify-center gap-3 p-4 rounded-xl bg-background border border-border hover:border-primary/50 transition-colors" data-testid="contact-phone">
              <Phone className="h-5 w-5 text-primary" />
              <span className="text-foreground font-medium">249-444-5227</span>
            </a>
            <a href="mailto:support@truenorthmuslimcommunity.ca" className="flex items-center justify-center gap-3 p-4 rounded-xl bg-background border border-border hover:border-primary/50 transition-colors" data-testid="contact-email">
              <Mail className="h-5 w-5 text-primary" />
              <span className="text-foreground font-medium text-sm">support@truenorthmuslimcommunity.ca</span>
            </a>
            <a href="https://truenorthmuslimcommunity.ca" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-3 p-4 rounded-xl bg-background border border-border hover:border-primary/50 transition-colors" data-testid="contact-web">
              <Globe className="h-5 w-5 text-primary" />
              <span className="text-foreground font-medium text-sm">truenorthmuslimcommunity.ca</span>
            </a>
          </div>

          <Link to={donateHref(50, true)}>
            <Button size="lg" className="rounded-full text-base h-14 px-10 bg-primary text-primary-foreground hover:bg-primary/90 glow-teal" data-testid="final-cta">
              <Heart className="mr-2 h-5 w-5" /> Contribute Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PlaceToWorshipPage;
